import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	MenuItem,
	Switch,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createPgServiceRequest,
	resetPgSaveSuccess,
	updatePgServiceRequest,
} from 'redux/reducers/pgSettings/reducer';
import { selectPgSaveLoading, selectPgSaveSuccess } from 'redux/reducers/pgSettings/selectors';
import { TPgLegalEntity, TPgService, TPgServiceKind } from 'services/api/paymentGateway/types';
import { PG_CODE_REGEX, PG_SERVICE_KIND_OPTIONS } from '../../constants';

type TServiceDialogProps = {
	open: boolean;
	onClose: () => void;
	serviceToEdit: TPgService | null;
	legalEntities: TPgLegalEntity[];
};

type TServiceFormValues = {
	code: string;
	name: string;
	description: string;
	kind: TPgServiceKind;
	legalEntityId: number | '';
	isActive: boolean;
	sortOrder: string;
};

const validationSchema = Yup.object({
	code: Yup.string()
		.required('Code is required')
		.matches(PG_CODE_REGEX, 'Lowercase letters, digits and "_", 2–40 characters'),
	name: Yup.string().trim().required('Name is required').max(200, 'Max 200 characters'),
	description: Yup.string().max(1000, 'Max 1000 characters'),
	kind: Yup.string().oneOf(['invoice', 'prepayment']).required(),
	legalEntityId: Yup.number()
		.typeError('Legal entity is required')
		.required('Legal entity is required'),
	sortOrder: Yup.number()
		.typeError('Must be a whole number')
		.integer('Must be a whole number')
		.required('Sort order is required'),
});

export const ServiceDialog: FC<TServiceDialogProps> = ({
	open,
	onClose,
	serviceToEdit,
	legalEntities,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!serviceToEdit;
	const loading = useSelector(selectPgSaveLoading('service'));
	const success = useSelector(selectPgSaveSuccess('service'));

	const defaultLegalEntity = legalEntities.find((e) => e.isDefault) ?? legalEntities[0];

	const formik = useFormik<TServiceFormValues>({
		initialValues: {
			code: serviceToEdit?.code ?? '',
			name: serviceToEdit?.name ?? '',
			description: serviceToEdit?.description ?? '',
			kind: serviceToEdit?.kind ?? 'invoice',
			legalEntityId: serviceToEdit?.legalEntityId ?? defaultLegalEntity?.id ?? '',
			isActive: serviceToEdit?.isActive ?? true,
			sortOrder: String(serviceToEdit?.sortOrder ?? 0),
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				name: values.name.trim(),
				description: values.description.trim() || null,
				kind: values.kind,
				legalEntityId: Number(values.legalEntityId),
				isActive: values.isActive,
				sortOrder: Number(values.sortOrder),
			};
			if (isEdit && serviceToEdit) {
				dispatch(updatePgServiceRequest({ id: serviceToEdit.id, body }));
			} else {
				dispatch(createPgServiceRequest({ ...body, code: values.code.trim() }));
			}
		},
	});

	useEffect(() => {
		if (!success) return;
		dispatch(resetPgSaveSuccess('service'));
		formik.resetForm();
		onClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [success]);

	const handleClose = () => {
		if (loading) return;
		formik.resetForm();
		onClose();
	};

	const hasMappings = (serviceToEdit?.mappedServiceTypes.length ?? 0) > 0;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle color="primary">
				{isEdit ? `Edit service ${serviceToEdit?.code}` : 'Add service'}
			</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Code"
						name="code"
						value={formik.values.code}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.code && !!formik.errors.code}
						helperText={
							(formik.touched.code && formik.errors.code) ||
							(isEdit ? 'The code cannot be changed' : 'e.g. ppwr; cannot be changed later')
						}
						disabled={isEdit}
						size="small"
					/>
					<TextField
						label="Name"
						name="name"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && !!formik.errors.name}
						helperText={formik.touched.name && formik.errors.name}
						size="small"
					/>
					<TextField
						label="Description"
						name="description"
						value={formik.values.description}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.description && !!formik.errors.description}
						helperText={formik.touched.description && formik.errors.description}
						size="small"
						multiline
						minRows={2}
					/>
					<TextField
						select
						label="Kind"
						name="kind"
						value={formik.values.kind}
						onChange={formik.handleChange}
						size="small"
						helperText={
							hasMappings
								? 'A service with EAS service type mappings must stay "Invoice"'
								: 'Invoice — paid by EAS invoices; Prepayment — EU DDP prepayment'
						}
					>
						{PG_SERVICE_KIND_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						label="Legal entity"
						name="legalEntityId"
						value={formik.values.legalEntityId}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.legalEntityId && !!formik.errors.legalEntityId}
						helperText={formik.touched.legalEntityId && formik.errors.legalEntityId}
						size="small"
					>
						{legalEntities.map((entity) => (
							<MenuItem key={entity.id} value={entity.id}>
								{entity.name} ({entity.code})
							</MenuItem>
						))}
					</TextField>
					<TextField
						label="Sort order"
						name="sortOrder"
						type="number"
						value={formik.values.sortOrder}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.sortOrder && !!formik.errors.sortOrder}
						helperText={formik.touched.sortOrder && formik.errors.sortOrder}
						size="small"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={formik.values.isActive}
								onChange={(e) => {
									void formik.setFieldValue('isActive', e.target.checked);
								}}
								name="isActive"
							/>
						}
						label="Active"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
